import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import { ReactElement, useState } from 'react'
import { DeleteIcon, EditIcon, MigrateIcon } from 'src/v3/core/icons'
import {
  QuizLibraryItem,
  ShowNotesLibraryItem,
  CreateContentTypes,
} from '../../course/types'
import {
  getStepIcon,
  getStepIconColor,
} from '../../course/utils/step-type-helpers'

type LibraryRowBaseProps = {
  item: QuizLibraryItem | ShowNotesLibraryItem
  contentType: CreateContentTypes
  onEdit: () => void
  onDelete: () => void
  onMigrate: () => void
}

export default function LibraryRowBase({
  item,
  contentType,
  onEdit,
  onDelete,
  onMigrate,
}: LibraryRowBaseProps): ReactElement {
  let canDelete = item.courses.length === 0
  let [confirmMigrateOpen, setConfirmMigrateOpen] = useState(false)

  return (
    <>
      <Box
        className="flex gap-3 px-4 py-3 rounded border"
        sx={{ bgcolor: 'background.paperDark', borderColor: 'divider' }}
      >
        <Box
          sx={{
            color: getStepIconColor(contentType),
            flexShrink: 0,
            pt: '2px',
          }}
        >
          {getStepIcon(contentType)}
        </Box>

        <Box className="flex-1 min-w-0 flex flex-col gap-1">
          <Box className="flex items-start justify-between gap-2">
            <Box className="min-w-0 flex-1">
              <Box className="flex items-center gap-2 flex-wrap">
                <Typography variant="body2" className="font-semibold">
                  {item.name}
                </Typography>
                {contentType === 'quiz' && (
                  <Typography variant="caption" color="text.secondary">
                    ({(item as QuizLibraryItem).questionCount}{' '}
                    {(item as QuizLibraryItem).questionCount === 1
                      ? 'question'
                      : 'questions'}
                    )
                  </Typography>
                )}
                {item.isMigratable && (
                  <Typography
                    variant="caption"
                    color="warning.main"
                    data-tour="library-item-migrate-label"
                  >
                    Requires migration
                  </Typography>
                )}
              </Box>
              {item.description && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  className="truncate block"
                >
                  {item.description}
                </Typography>
              )}
            </Box>

            <Box className="flex items-center gap-1 flex-shrink-0">
              {item.isMigratable && item.courses.length > 0 && (
                <Tooltip title="Migrate">
                  <IconButton
                    size="small"
                    color="warning"
                    onClick={() => setConfirmMigrateOpen(true)}
                    aria-label={`Migrate ${item.name}`}
                  >
                    <MigrateIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip
                title={item.isMigratable ? 'Migrate before editing' : 'Edit'}
              >
                <span>
                  <IconButton
                    size="small"
                    onClick={onEdit}
                    disabled={item.isMigratable}
                    aria-label={`Edit ${item.name}`}
                  >
                    <EditIcon
                      fontSize="small"
                      color={item.isMigratable ? 'disabled' : 'primary'}
                    />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  canDelete
                    ? 'Delete'
                    : 'Remove course associations before deleting'
                }
              >
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={onDelete}
                    disabled={!canDelete}
                    aria-label={`Delete ${item.name}`}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>

          {item.courses.length === 0 ? (
            <Typography variant="caption" color="text.disabled">
              No Course Association
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">
              <span style={{ fontWeight: 600 }}>Associated Courses: </span>
              {item.courses.map((c) => c.courseName).join(', ')}
            </Typography>
          )}
        </Box>
      </Box>
      <Dialog
        open={confirmMigrateOpen}
        onClose={() => setConfirmMigrateOpen(false)}
        fullWidth
        aria-labelledby={`confirm-migrate-dialog-title-${item.id}`}
        maxWidth="xs"
      >
        <DialogTitle id={`confirm-migrate-dialog-title-${item.id}`}>
          Confirm Migration
        </DialogTitle>
        <DialogContent>
          <Typography>
            Migrate <strong>{item.name}</strong> to the latest version? This
            will not affect current functionality.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmMigrateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              setConfirmMigrateOpen(false)
              onMigrate()
            }}
          >
            Migrate
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
